/**
 * Code-owned model availability contract (VM-966).
 *
 * Availability is source-of-truth in code, not the database. There is no admin UI
 * toggle and no DB column — changing a model's availability requires a code PR. This
 * file defines the enum, the per-model shape, and the resolver that the admin model
 * registry (and any future consumer) uses to derive a single availability verdict.
 *
 * Precedence (highest wins):
 *   1. explicit `availability` field on the config  → source "explicit"
 *   2. legacy `isLegacy: true` flag                  → source "legacy-flag" (auto LEGACY)
 *   3. nothing set                                    → source "default"  (ENABLED)
 */

/** Availability state of an integrated model. String values (never numeric). */
export enum EModelAvailability {
  /** Wired and offered to users. */
  ENABLED = "enabled",
  /** Intentionally turned off (kept in code, not offered). */
  DISABLED = "disabled",
  /** Superseded / deprecated but still runnable — kept for back-compat. */
  LEGACY = "legacy",
  /** Announced but not yet wired for real traffic. */
  COMING_SOON = "coming-soon",
}

/** Modality bucket a model belongs to. No such enum existed before (EModelTag is stale). */
export enum EModelModality {
  VIDEO = "video",
  IMAGE = "image",
  AUDIO = "audio",
  TEXT = "text",
}

/**
 * Explicit availability annotation on a model config. JSON-serializable (no functions),
 * so it can flow straight through a tRPC boundary into the admin registry table.
 */
export interface ModelAvailability {
  status: EModelAvailability
  /** Human-readable why (e.g. "Retired Jan 2026", "beta — invite only"). */
  reason?: string
  /** Optional code pointer for the read-only admin banner (e.g. "video/kling.ts"). */
  sourceRef?: string
}

/** Which rule produced the resolved availability — surfaced in the admin UI. */
export type AvailabilitySource = "explicit" | "legacy-flag" | "default"

/**
 * Minimal shape the resolver reads. Both the model UI configs (`BaseModelConfig`) and
 * the LLM metadata rows (`LLMModelMeta`) satisfy this — neither is imported here to keep
 * this module dependency-free (avoids import cycles through the config/pricing graph).
 */
export interface AvailabilityResolvable {
  availability?: ModelAvailability
  isLegacy?: boolean
}

const LEGACY_FLAG_REASON = "Marked legacy via isLegacy flag (auto-derived)"

/**
 * Resolve a single availability verdict from a config, honoring the precedence above.
 * Pure and total — always returns a verdict plus the source rule that produced it.
 */
export const resolveAvailability = (
  config: AvailabilityResolvable,
): { availability: ModelAvailability; source: AvailabilitySource } => {
  if (config.availability) {
    return { availability: config.availability, source: "explicit" }
  }
  if (config.isLegacy === true) {
    return {
      availability: { status: EModelAvailability.LEGACY, reason: LEGACY_FLAG_REASON },
      source: "legacy-flag",
    }
  }
  return { availability: { status: EModelAvailability.ENABLED }, source: "default" }
}
